import { externalMacroName } from './graphCodec'
import type { ExternalMacro, FlowEdge, FlowNode, MacroOption, NodeType } from './types'

type GraphLike<N> = {
  nodes: N[]
  edges: Array<Pick<FlowEdge, 'fromNodeId' | 'toNodeId'>>
}

/**
 * Finds the first macro-end reachable from a macro-start's outputs without crossing
 * into another macro-start (macros do not nest).
 */
export function findMacroEnd<N extends { id: string; type: NodeType }>(
  graph: GraphLike<N>,
  macroStartNodeId: string,
): N | null {
  const byId = new Map(graph.nodes.map((node) => [node.id, node]))
  const visited = new Set<string>([macroStartNodeId])
  const stack = graph.edges
    .filter((edge) => edge.fromNodeId === macroStartNodeId)
    .map((edge) => edge.toNodeId)
  while (stack.length) {
    const id = stack.pop()!
    if (visited.has(id)) continue
    visited.add(id)
    const node = byId.get(id)
    if (!node) continue
    if (node.type === 'macro-end') return node
    if (node.type === 'macro-start') continue
    for (const edge of graph.edges.filter((edge) => edge.fromNodeId === id))
      stack.push(edge.toNodeId)
  }
  return null
}

/** Port counts of host-supplied macros, keyed by the name a macro-call uses. */
export function externalMacroOptions(macros: ExternalMacro[]): MacroOption[] {
  return macros
    .map((macro) => {
      const name = externalMacroName(macro)
      if (!name) return null
      const start = macro.flowGraph.nodes.find((node) => node.type === 'macro-start')
      const end = start ? findMacroEnd(macro.flowGraph, start.id) : null
      return {
        name,
        inputCount:
          typeof start?.data?.inputCount === 'number' ? Math.max(1, start.data.inputCount) : 1,
        outputCount:
          typeof end?.data?.outputCount === 'number' ? Math.max(1, end.data.outputCount) : 1,
      }
    })
    .filter((item): item is MacroOption => Boolean(item))
}

/** All macros a macro-call may reference; local definitions override host macros. */
export function macroOptions(
  graph: { nodes: FlowNode[]; edges: FlowEdge[] },
  external: MacroOption[],
): MacroOption[] {
  const byName = new Map<string, MacroOption>()
  for (const macro of external) byName.set(macro.name, macro)
  for (const node of graph.nodes) {
    if (node.type !== 'macro-start' || !node.data.macroName) continue
    byName.set(node.data.macroName, {
      name: node.data.macroName,
      inputCount: node.data.inputCount ?? 1,
      outputCount: macroOutputCount(graph, node.data.macroName),
    })
  }
  return [...byName.values()]
}

/** Output count of a local macro; the first macro-start with that name defines it. */
export function macroOutputCount(graph: { nodes: FlowNode[]; edges: FlowEdge[] }, name: string) {
  const start = graph.nodes.find((n) => n.type === 'macro-start' && n.data.macroName === name)
  if (!start) return 1
  return findMacroEnd(graph, start.id)?.data.outputCount ?? 1
}
